import { timeToMinutes, travelOf, type Occurrence } from '@planer/shared';

export interface PlacedBlock {
  occ: Occurrence;
  /** Minutes from local midnight. */
  startMin: number;
  /** Visual length in minutes (never shorter than the minimum, so titles stay readable). */
  spanMin: number;
  column: number;
  columns: number;
  /** Travel drawn above / below the block (minutes, clamped to the day). */
  travelBefore: number;
  travelAfter: number;
}

/**
 * Lays out a day like a calendar: each block is as tall as it lasts; blocks that overlap in time
 * share the width in columns (classic "cluster + first free column" algorithm).
 */
export function layoutDay(occs: readonly Occurrence[], minSpanMin = 25): PlacedBlock[] {
  const blocks = occs
    .map((occ) => {
      const startMin = timeToMinutes(occ.startTime);
      const spanMin = Math.max(minSpanMin, Math.min(occ.durationMin, 24 * 60 - startMin));
      const travelBefore = Math.min(travelOf(occ).before, startMin);
      const travelAfter = Math.max(0, Math.min(travelOf(occ).after, 24 * 60 - startMin - spanMin));
      return { occ, startMin, spanMin, column: 0, columns: 1, travelBefore, travelAfter };
    })
    .sort((a, b) => outerStart(a) - outerStart(b) || b.spanMin - a.spanMin);

  let cluster: PlacedBlock[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -1;

  const closeCluster = () => {
    const columns = Math.max(1, columnEnds.length);
    for (const b of cluster) b.columns = columns;
    cluster = [];
    columnEnds = [];
  };

  // The drive there and back takes room too: blocks whose travel overlaps sit side by side.
  for (const b of blocks) {
    const from = outerStart(b);
    const to = b.startMin + b.spanMin + b.travelAfter;
    if (from >= clusterEnd) closeCluster();
    let col = columnEnds.findIndex((end) => end <= from);
    if (col === -1) {
      col = columnEnds.length;
      columnEnds.push(0);
    }
    columnEnds[col] = to;
    b.column = col;
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, to);
  }
  closeCluster();
  return blocks;
}

const outerStart = (b: Pick<PlacedBlock, 'startMin' | 'travelBefore'>) =>
  b.startMin - b.travelBefore;

/** First hour to show: 6:00, or earlier if something (or the drive to it) starts before. */
export function firstVisibleHour(blocks: readonly PlacedBlock[]): number {
  const earliest = Math.min(...blocks.map(outerStart), 6 * 60);
  return Math.floor(earliest / 60);
}
